import {User} from '@hconnect/apiclient'
import {Button, LabelRegular} from '@hm/fundamental'
import {mdiPlus} from '@mdi/js'
import React from 'react'
import {useFormContext} from 'react-hook-form'

import {useTranslation} from '../../../hooks/useTranslation'
import {RoleCustomerLookupTextField} from '../../../layouts/RoleCustomerLookupTextField'
import {RolePayerLookupTextField} from '../../../layouts/RolePayerLookupTextField'
import {CommentCategories} from '../../../layouts/Roles/CommentCategories'
import {CustomersAndProjectsSites} from '../../../layouts/Roles/CustomersScopes'
import {Hauliers} from '../../../layouts/Roles/Hauliers'
import {Payers} from '../../../layouts/Roles/Payers'
import {Plants} from '../../../layouts/Roles/Plants'
import {DeleteDataScope} from '../../../layouts/Roles/role-view/DeleteDataScope'
import {UnsupportedDataScope} from '../../../layouts/Roles/role-view/UnsupportedDataScope'
import {UpmPlants} from '../../../layouts/Roles/UpmPlants'
import {isWebhookSectionVisible} from '../../../layouts/Roles/utils'
import {WebhookSubscriptions} from '../../../layouts/Roles/WebhookSubscriptions'
import {
  getDataScopeConfiguration,
  LookupDataScopeInput
} from '../../../modules/DataScopeConfiguration'
import {
  ADD_CUSTOMER,
  ADD_PAYER,
  DELETE_COMMENT_CATEGORY,
  DELETE_CUSTOMER,
  DELETE_HAULIER,
  DELETE_PAYER,
  DELETE_PLANT,
  GroupedRoleReducerAction,
  REPLACE_CUSTOMERS_AND_PROJECTS_SITES
} from '../../../modules/GroupedRoleAssignmentReducer'
import {Permission} from '../../../modules/LoggedInUser'
import {GroupedRoleAssignment, RoleAssignment} from '../../../modules/ManageUsers.selectors'

type ExpandedSectionsProps = {
  groupedRoleAssignment: GroupedRoleAssignment
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
  isEditing: boolean
  showCustomerInput: boolean
  setShowCustomerInput: (show: boolean) => void
  showCustomerDropdown: boolean
  setShowCustomerDropdown: (show: boolean) => void
  isUserAdminPilot: boolean
  hasRoleOrders: boolean
  customerAdminData?: Record<string, string | undefined>
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
  unsupportedDataScopes?: string[]
  roleAssignments?: RoleAssignment[] | null
}

type SectionConfig = {
  condition: boolean
  component: React.ReactNode
}

// eslint-disable-next-line complexity
export const ExpandedSections = ({
  groupedRoleAssignment,
  dispatchRoleStateUpdate,
  isEditing,
  showCustomerInput,
  setShowCustomerInput,
  showCustomerDropdown,
  setShowCustomerDropdown,
  customerAdminData,
  loggedInUserPermissions,
  loggedInUserProfile,
  isUserAdminPilot,
  unsupportedDataScopes,
  hasRoleOrders,
  roleAssignments
}: ExpandedSectionsProps) => {
  const {t} = useTranslation()
  const {watch, formState} = useFormContext<GroupedRoleAssignment>()

  const formValues = watch()

  const payerConfiguration = getDataScopeConfiguration(
    'payerIds',
    isUserAdminPilot,
    formValues.roleType
  ) as LookupDataScopeInput

  const customerConfiguration = getDataScopeConfiguration(
    'customerIds',
    isUserAdminPilot,
    formValues.roleType
  ) as LookupDataScopeInput

  const customerErrorMessage = formState.isSubmitted
    ? formState.errors?.variantDataScopes?.customersScopes?.message
    : null
  const payerErrorMessage = formState.isSubmitted
    ? formState.errors?.variantDataScopes?.payerIds?.message
    : null

  const customerDependenciesMet =
    customerConfiguration.dependsOn !== undefined
      ? !customerConfiguration.dependsOn
          .map((dep) => formValues.commonDataScopes[dep])
          .some((dependency) => dependency === undefined)
      : true

  const payerDependenciesMet =
    payerConfiguration.dependsOn !== undefined
      ? !customerConfiguration.dependsOn
          .map((dep) => formValues.commonDataScopes[dep])
          .some((dependency) => dependency === undefined)
      : true

  const customerAdminPayerDependenciesMet =
    isUserAdminPilot && groupedRoleAssignment.variantDataScopes.customersScopes.length === 0

  const {variantDataScopes} = groupedRoleAssignment

  const hasItems = (scopeName: keyof typeof variantDataScopes): boolean => {
    const scope = variantDataScopes[scopeName] || groupedRoleAssignment.commonDataScopes[scopeName]
    return Array.isArray(scope) ? scope.length > 0 : false
  }

  const sectionConfigs: SectionConfig[] = [
    {
      condition: hasItems('customersScopes'),
      component: (
        <>
          <LabelRegular className="text-foreground_faded">
            {t('roleAssignment.customersAndSitesSectionHeader')}
          </LabelRegular>
          <div className="mt-2 w-full flex flex-col gap-4 max-w-[600px]">
            <CustomersAndProjectsSites
              groupedRoleAssignment={groupedRoleAssignment}
              loggedInUserProfile={loggedInUserProfile}
              isEditing={isEditing}
              handleDeleteCustomer={(data) => {
                dispatchRoleStateUpdate({
                  type: DELETE_CUSTOMER,
                  payload: data.customerId
                })
              }}
              handleSaveContractIds={() => {}}
              handleSaveCustomerId={(customersScopes) => {
                dispatchRoleStateUpdate({
                  type: REPLACE_CUSTOMERS_AND_PROJECTS_SITES,
                  payload: customersScopes
                })
              }}
            />
            {showCustomerInput && isEditing && (
              <RoleCustomerLookupTextField
                customers={[]}
                groupedRoleAssignment={groupedRoleAssignment}
                onChangeCustomerIds={(customerIds) => {
                  dispatchRoleStateUpdate({
                    type: ADD_CUSTOMER,
                    payload: customerIds[0]
                  })
                  setShowCustomerInput(false)
                }}
                disabled={
                  isUserAdminPilot ? !customerAdminPayerDependenciesMet : !payerDependenciesMet
                }
                name="customerIds"
                helperText={customerErrorMessage || ''}
                dataScopeName="customerIds"
                customerAdminData={customerAdminData}
                showCustomerOrPayerDropdown={showCustomerDropdown}
                setShowCustomerOrPayerDropdown={setShowCustomerDropdown}
                loggedInUserPermissions={loggedInUserPermissions}
              />
            )}
            {isEditing && hasItems('customersScopes') && (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => setShowCustomerInput(true)}
                  icon={mdiPlus}
                  type="button"
                >
                  Add Customer ID
                </Button>
              </div>
            )}
          </div>
        </>
      )
    },
    {
      condition: hasItems('payerIds'),
      component: (
        <div>
          <Payers
            groupedRoleAssignment={groupedRoleAssignment}
            isEditing={isEditing}
            handleDeletePayer={(payerId) => {
              dispatchRoleStateUpdate({
                type: DELETE_PAYER,
                payload: payerId
              })
            }}
          />
          {showCustomerInput && isEditing && (
            <RolePayerLookupTextField
              payers={[]}
              groupedRoleAssignment={groupedRoleAssignment}
              onChangePayerIds={(payerIds) => {
                dispatchRoleStateUpdate({
                  type: ADD_PAYER,
                  payload: payerIds[0]
                })
                setShowCustomerInput(false)
              }}
              disabled={isUserAdminPilot ? !isUserAdminPilot : !customerDependenciesMet}
              name="payerIds"
              helperText={payerErrorMessage || ''}
              dataScopeName="payerIds"
              customerAdminData={customerAdminData}
              setShowCustomerOrPayerDropdown={setShowCustomerDropdown}
              loggedInUserPermissions={loggedInUserPermissions}
            />
          )}
          {isEditing && hasItems('payerIds') && (
            <div>
              <Button
                variant="secondary"
                onClick={() => setShowCustomerInput(true)}
                icon={mdiPlus}
                className="mt-4"
              >
                Add Payer Id
              </Button>
            </div>
          )}
        </div>
      )
    },
    {
      condition: hasItems('plantIds'),
      component: (
        <Plants
          groupedRoleAssignment={groupedRoleAssignment}
          isEditing={isEditing}
          handleDeletePlant={(plantId: string) => {
            dispatchRoleStateUpdate({
              type: DELETE_PLANT,
              payload: plantId
            })
          }}
        />
      )
    },
    {
      condition: !isEditing && (hasItems('upmIds') || hasItems('upmHierarchyIds')),
      component: <UpmPlants groupedRoleAssignment={groupedRoleAssignment} />
    },
    {
      condition: hasItems('commentCategories'),
      component: (
        <CommentCategories
          groupedRoleAssignment={groupedRoleAssignment}
          isEditing={isEditing}
          handleDeleteCommentCategory={(commentCategory) =>
            dispatchRoleStateUpdate({type: DELETE_COMMENT_CATEGORY, payload: commentCategory})
          }
        />
      )
    },
    {
      condition: hasItems('haulierIds'),
      component: (
        <Hauliers
          groupedRoleAssignment={groupedRoleAssignment}
          isEditing={isEditing}
          handleDeleteHaulier={(haulierId) =>
            dispatchRoleStateUpdate({
              type: DELETE_HAULIER,
              payload: haulierId
            })
          }
        />
      )
    },
    {
      condition: isWebhookSectionVisible(groupedRoleAssignment, isEditing),
      component: (
        <WebhookSubscriptions
          groupedRoleAssignment={groupedRoleAssignment}
          isEditing={isEditing}
          dispatchRoleStateUpdate={dispatchRoleStateUpdate}
        />
      )
    },
    {
      condition: (unsupportedDataScopes?.length || 0) > 0,
      component: unsupportedDataScopes?.map((dataScopeName) => {
        if (hasRoleOrders) {
          return (
            <DeleteDataScope
              key={dataScopeName}
              dataScopeName={dataScopeName}
              dataScopeValues={groupedRoleAssignment.unsupportedDataScopes.orderIds as string[]}
              groupedRoleAssignment={groupedRoleAssignment}
              roleAssignments={roleAssignments || []}
            />
          )
        }
        return (
          <UnsupportedDataScope
            key={dataScopeName}
            dataScopeName={dataScopeName}
            groupedRoleAssignment={groupedRoleAssignment}
          />
        )
      })
    }
  ]

  return (
    <div className="w-full mt-4">
      {sectionConfigs
        .filter(({condition}) => condition)
        .map(({component}, index) => (
          <div key={index} className={index === 0 ? '' : 'mt-2'}>
            {component}
          </div>
        ))}
    </div>
  )
}
